import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchStockMovements } from "../services/productService";
import { formatDateTime } from "../services/datetime";
import { labelFor } from "../services/enumLabels";

/** Read-only ledger for one product at one branch. */
export default function MovementHistoryModal({ branchId, product, open, onClose }) {
  const { t, i18n } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const productId = product?.id;

  const load = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    setError("");
    try {
      const { movements: list, meta: pagination } = await fetchStockMovements(
        branchId,
        productId,
        page
      );
      setMovements(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("products.history.loadFailed")));
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, productId, page, t]);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, productId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-2xl rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold">{t("products.history.title")}</h2>
            <p className="text-xs text-ink-2">{product?.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="rounded p-1 text-ink-muted hover:bg-raised hover:text-ink-2"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
              {error}
            </div>
          )}

          {loading ? (
            <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
          ) : movements.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-2">{t("products.history.empty")}</p>
          ) : (
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="py-2 pe-3">{t("products.history.when")}</th>
                  <th className="py-2 pe-3">{t("products.history.type")}</th>
                  <th className="py-2 pe-3">{t("products.history.change")}</th>
                  <th className="py-2 pe-3">{t("products.history.after")}</th>
                  <th className="py-2 pe-3">{t("products.history.reason")}</th>
                  <th className="py-2">{t("products.history.by")}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id} className="border-b border-line last:border-0">
                    <td className="py-2 pe-3 text-xs text-ink-2">
                      {formatDateTime(movement.created_at, i18n.language)}
                    </td>
                    <td className="py-2 pe-3">
                      <span className="rounded-full bg-raised px-2 py-1 text-xs text-ink">
                        {labelFor("stock_movement", movement.type)}
                      </span>
                    </td>
                    <td
                      className={`py-2 pe-3 font-medium ${
                        movement.quantity >= 0 ? "text-accent-text" : "text-danger-text"
                      }`}
                      dir="ltr"
                    >
                      {movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}
                    </td>
                    <td className="py-2 pe-3 text-ink-2" dir="ltr">
                      {movement.resulting_quantity}
                    </td>
                    <td className="py-2 pe-3 text-ink-2">
                      {movement.reason || "—"}
                      {movement.reference && (
                        <span className="ms-1 text-xs text-ink-2" dir="ltr">
                          ({movement.reference})
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-ink-2">{movement.user?.name ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {meta && meta.last_page > 1 && (
          <div className="flex items-center justify-center gap-2 border-t border-line px-5 py-3">
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("common.previous")}
            </button>
            <span className="text-sm text-ink-2">
              {t("common.pageOf", { current: meta.current_page, last: meta.last_page })}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
